import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  AUTO_FILL_COMPANY_EMAIL,
  AUTO_FILL_MAX_LEVEL,
  getLevelAmount,
  parseEnrollmentSeqFromProgramId,
  uplineEnrollmentIndexForMatrixFillSeq,
} from "../../config/autoFillProgramConfig";
import { registerMatrixFillForAfpEnrollment } from "./autoFillMatrixFillRouting";
import { resolveMatrixUplineProgramSync } from "./autoFillMatrixUplineResolve";
import { ROOT_SPAWN_FILL_SOURCE, MAHESH_L1_ROSTER_UPGRADE_FILLERS } from "../../config/autoFillRootSpawnConfig";
import {
  expectedMaheshL2FillerProgramId,
  isMaheshL2SerialFlow,
  MAHESH_AUTO_FILL_PROGRAM_ID,
} from "../../config/autoFillMaheshL2SerialConfig";
import {
  applyMaheshL2SerialPaymentCredit,
  maheshL2SerialCycleFilled,
} from "./maheshL2SerialRouting";
import { buildTransactionId, recordAutoFillLedgerEntry } from "./autoFillLedger";
import {
  AUTO_FILL_FORFEITURE_LEVEL_MAX,
  resolveForfeitureLevelMin,
} from "../../config/autoFillLevelForfeitureConfig";
import {
  buildLevelForfeitureFirestorePatch,
  getLevelForfeitureState,
  initialLevelForfeitureState,
  recordLevelPointsCredited,
} from "./autoFillLevelForfeitureTracking";
import {
  GENESIS_AUTO_FILL_PROGRAM_ID,
  qualifiesForGenesisStyleL1ToL2Activation,
} from "./autoFillProgramSpawn";

async function nextProgramSequence() {
  const counterRef = doc(db, "autoFillMeta", "counters");
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const next = (snap.exists() ? snap.data().programSeq || 0 : 0) + 1;
    tx.set(counterRef, { programSeq: next }, { merge: true });
    return next;
  });
}

export async function generateProgramPublicId() {
  const { programPublicId } = await allocateProgramIdentity();
  return programPublicId;
}

export async function allocateProgramIdentity() {
  const enrollmentSeq = await nextProgramSequence();
  return {
    enrollmentSeq,
    programPublicId: `AFP-${String(enrollmentSeq).padStart(6, "0")}`,
  };
}

async function getUserDocByUid(uid) {
  const snap = await getDocs(
    query(collection(db, "users"), where("uid", "==", uid))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

export async function findActiveProgramByEnrollmentSeq(enrollmentSeq) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillPrograms"),
      where("enrollmentSeq", "==", Number(enrollmentSeq))
    )
  );
  if (snap.empty) return null;
  const programs = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.status === "active" || p.status === "on_hold");
  programs.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  );
  return programs[0];
}

async function findOpenMatrixPosition(programFirestoreId, level) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillMatrixPositions"),
      where("programDocId", "==", programFirestoreId),
      where("level", "==", level),
      where("positionStatus", "==", "open")
    )
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

async function createLevelPositions(programDocId, programPublicId, userId, level) {
  for (let i = 1; i <= 3; i += 1) {
    await addDoc(collection(db, "autoFillMatrixPositions"), {
      programDocId,
      programId: programPublicId,
      userId,
      level,
      positionIndex: i,
      positionStatus: "open",
      filledByUserId: null,
      createdAt: new Date().toISOString(),
    });
  }
}

async function getAdminCoPointsAccount() {
  const snap = await getDocs(
    query(
      collection(db, "users"),
      where("email", "==", AUTO_FILL_COMPANY_EMAIL)
    )
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

/** Genesis AFP #1 (company only): entry ₹150 → Admin CO Points. */
async function creditGenesisAdminCoPoints(amount, meta) {
  const admin = await getAdminCoPointsAccount();
  if (!admin) {
    console.error("Admin CO account missing:", AUTO_FILL_COMPANY_EMAIL);
    return;
  }
  const co = admin.data.autoFillCoPoints || {};
  await updateDoc(doc(db, "users", admin.id), {
    autoFillCoPoints: {
      earned: (co.earned || 0) + amount,
      available: (co.available || 0) + amount,
      withdrawn: co.withdrawn || 0,
    },
    updatedAt: new Date().toISOString(),
  });

  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: meta.programId,
    userId: admin.data.uid,
    beneficiaryProgramOwnerId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "CO_POINTS",
    fromWallet: "EXTERNAL",
    toWallet: "ADMIN_CO_POINTS",
    reference: meta.reference,
  });
}

/** Matrix payment 1 of 3 (all levels) → program owner CO Points (withdrawal). */
async function creditUserPointsWallet(userDocRef, userData, amount, meta) {
  const points = userData.autoFillPoints || {};
  await updateDoc(userDocRef, {
    autoFillPoints: {
      ...points,
      earned: (points.earned || 0) + amount,
      available: (points.available || 0) + amount,
      withdrawn: points.withdrawn || 0,
      locked: points.locked || 0,
    },
    updatedAt: new Date().toISOString(),
  });
  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: meta.programId,
    userId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "POINTS",
    fromWallet: meta.fromWallet || "MATRIX_POOL",
    toWallet: "POINTS",
    matrixPositionId: meta.matrixPositionId || null,
    fillerUserId: meta.fillerUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || null,
  });
}

/** L1→L2+ activation: AFP #1 upgrade → Admin CO; others → AFP #1 upgrade pool (not points). */
async function creditAdminCoFromLevelActivation(amount, meta) {
  const admin = await getAdminCoPointsAccount();
  if (!admin) {
    console.error("Admin CO account missing:", AUTO_FILL_COMPANY_EMAIL);
    return;
  }
  const co = admin.data.autoFillCoPoints || {};
  await updateDoc(doc(db, "users", admin.id), {
    autoFillCoPoints: {
      earned: (co.earned || 0) + amount,
      available: (co.available || 0) + amount,
      withdrawn: co.withdrawn || 0,
    },
    updatedAt: new Date().toISOString(),
  });

  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: meta.programId,
    userId: admin.data.uid,
    beneficiaryProgramOwnerId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "CO_POINTS",
    fromWallet: "UPGRADE",
    toWallet: "ADMIN_CO_POINTS",
    fillerUserId: meta.activatingUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || null,
  });
}

/** Level unlock uses only matrix payments #2 + #3; no spillover to uplines. Genesis own unlock → Admin CO. */
async function routeLevelActivationPayout(program, activationCost, nextLevel) {
  if (Number(nextLevel) !== 2) return;
  if (!qualifiesForGenesisStyleL1ToL2Activation(program)) return;

  await creditAdminCoFromLevelActivation(activationCost, {
    programId: program.programId,
    userId: program.userId,
    activatingUserId: program.userId,
    level: nextLevel,
    reference: `Level ${nextLevel} activation — matrix reserve → Admin CO (${program.programId})`,
    metadata: { sourceProgramId: program.programId, kind: "LEVEL_ACTIVATION_PAYOUT" },
  });
}

async function creditUserUpgradePool(userDocRef, userData, amount, meta) {
  const upgrade = userData.autoFillUpgrade || {};
  const reserved = (upgrade.reserved || 0) + amount;
  await updateDoc(userDocRef, {
    autoFillUpgrade: {
      ...upgrade,
      reserved,
      used: upgrade.used || 0,
    },
    updatedAt: new Date().toISOString(),
  });
  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: meta.programId,
    userId: meta.userId,
    level: meta.level,
    amount,
    transactionType: "LEVEL_UPGRADE",
    fromWallet: meta.fromWallet || "MATRIX_POOL",
    toWallet: "UPGRADE",
    matrixPositionId: meta.matrixPositionId || null,
    fillerUserId: meta.fillerUserId || null,
    reference: meta.reference,
    metadata: meta.metadata || { targetLevel: meta.level + 1 },
  });
}

async function countFilledMatrixPaymentsAtLevel(programDocId, level) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillMatrixPositions"),
      where("programDocId", "==", programDocId),
      where("level", "==", Number(level)),
      where("positionStatus", "==", "filled")
    )
  );
  return snap.size;
}

/** When upgrade pool ≥ 2× level payment and matrix cycle complete, advance (genesis + all member/spawn AFPs). */
export async function maybeAdvanceProgramFromUpgradePool(
  programDocId,
  programHint = null
) {
  let program = programHint;
  if (!program?.programId) {
    const snap = await getDoc(doc(db, "autoFillPrograms", programDocId));
    if (!snap.exists()) return { advancedLevels: 0 };
    program = { id: snap.id, ...snap.data() };
  }

  const enrollmentSeq =
    program.enrollmentSeq ??
    parseEnrollmentSeqFromProgramId(program.programId);
  const isGenesisRoot = Number(enrollmentSeq) === 1;

  if (!isGenesisRoot) {
    const level = Number(program.currentLevel) || 1;
    const filled = await countFilledMatrixPaymentsAtLevel(programDocId, level);
    if (filled < 3) {
      return {
        advancedLevels: 0,
        currentLevel: program.currentLevel || 1,
        reason: "matrix_cycle_incomplete",
      };
    }
  }

  let advancedLevels = 0;
  for (let step = 0; step < AUTO_FILL_MAX_LEVEL; step += 1) {
    const userDoc = await getUserDocByUid(program.userId);
    if (!userDoc) break;

    const result = await tryAdvanceProgramLevel(programDocId, program, userDoc);
    if (!result?.advanced) break;

    advancedLevels += 1;
    const refreshed = await getDoc(doc(db, "autoFillPrograms", programDocId));
    if (!refreshed.exists()) break;
    program = { id: refreshed.id, ...refreshed.data() };
  }

  return { advancedLevels, currentLevel: program.currentLevel || 1 };
}

/** @deprecated use maybeAdvanceProgramFromUpgradePool */
export const maybeAdvanceGenesisProgramFromUpgradePool =
  maybeAdvanceProgramFromUpgradePool;

export async function tryAdvanceProgramLevel(programDocId, program, userDoc) {
  const currentLevel = program.currentLevel || 1;
  if (currentLevel >= AUTO_FILL_MAX_LEVEL) return;

  const nextLevel = currentLevel + 1;

  // Matrix plan: fills 2–3 at current level fund 2× Auto Fill(L) → activate L+1.
  const activationCost = getLevelAmount(currentLevel) * 2;
  const upgrade = userDoc.data.autoFillUpgrade || {};
  if ((upgrade.reserved || 0) < activationCost) {
    return { advanced: false, reason: "insufficient_upgrade" };
  }

  await updateDoc(doc(db, "users", userDoc.id), {
    autoFillUpgrade: {
      ...upgrade,
      reserved: (upgrade.reserved || 0) - activationCost,
      used: (upgrade.used || 0) + activationCost,
    },
    updatedAt: new Date().toISOString(),
  });

  const programLevelPatch = {
    currentLevel: nextLevel,
    status: "active",
    holdReason: null,
    holdAtLevel: null,
    levelPaymentsInCycle: 0,
    updatedAt: new Date().toISOString(),
  };
  const forfeitureMin = resolveForfeitureLevelMin(program);
  if (
    nextLevel >= forfeitureMin &&
    nextLevel <= AUTO_FILL_FORFEITURE_LEVEL_MAX &&
    !getLevelForfeitureState(program, nextLevel)?.reachedAt
  ) {
    const reachedAt = new Date().toISOString();
    const initState = initialLevelForfeitureState(reachedAt);
    Object.assign(
      programLevelPatch,
      buildLevelForfeitureFirestorePatch(program, nextLevel, initState)
    );
  }
  await updateDoc(doc(db, "autoFillPrograms", programDocId), programLevelPatch);

  await createLevelPositions(
    programDocId,
    program.programId,
    program.userId,
    nextLevel
  );

  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: program.programId,
    userId: program.userId,
    level: nextLevel,
    amount: activationCost,
    transactionType: "LEVEL_ACTIVATED",
    fromWallet: "UPGRADE",
    toWallet: "LEVEL",
    reference: `Activated level ${nextLevel}`,
  });

  // Genesis L1→L2 only: 2×₹150 (₹300) activation → Admin CO. L2→L3 uses member wallet + upgrade pool.
  const enrollmentSeq =
    program.enrollmentSeq ??
    parseEnrollmentSeqFromProgramId(program.programId);
  if (qualifiesForGenesisStyleL1ToL2Activation(program) && currentLevel === 1) {
    await routeLevelActivationPayout(program, activationCost, nextLevel);
  }

  const advanced = { advanced: true, newLevel: nextLevel };

  if (Number(enrollmentSeq) === 1 && nextLevel === 2) {
    const { maybeRunInfinityWhaleLevel2AutoUpgrade } = await import(
      "./infinityWhaleL2AutoUpgrade.js"
    );
    await maybeRunInfinityWhaleLevel2AutoUpgrade(
      programDocId,
      { ...program, ...programLevelPatch, currentLevel: nextLevel },
      { processMatrixFillForUpline, findActiveProgramByEnrollmentSeq }
    );
  }

  const isGenesisSpawnChild =
    program.spawnedFromWithdrawal === true &&
    program.parentProgramId === GENESIS_AUTO_FILL_PROGRAM_ID;

  if (Number(enrollmentSeq) !== 1) {
    const skipMemberActivation =
      isGenesisSpawnChild && currentLevel === 1 && nextLevel === 2;
    if (!skipMemberActivation) {
      const { routeMemberLevelActivationMatrixFill } = await import(
        "./autoFillActivationMatrixRouting.js"
      );
      await routeMemberLevelActivationMatrixFill({
        activatingProgram: { ...program, ...programLevelPatch, currentLevel: nextLevel },
        activatingProgramDocId: programDocId,
        fromLevel: currentLevel,
        processMatrixFillForUpline,
        findActiveProgramByEnrollmentSeq,
      });
    }
  }

  return advanced;
}

export async function processMatrixFillForUpline({
  uplineProgram,
  uplineProgramDocId,
  fillerUserId,
  fillerProgramId,
  orderId,
  fillSource = "AFP_ENROLLMENT",
  matrixFillSeq = null,
  matrixLevel = null,
}) {
  const level =
    matrixLevel != null
      ? Number(matrixLevel)
      : uplineProgram.currentLevel || 1;
  const position = await findOpenMatrixPosition(uplineProgramDocId, level);
  if (!position) return { filled: false, reason: "no_open_position" };

  const paymentsInCycle = (uplineProgram.levelPaymentsInCycle || 0) + 1;
  const paymentIndexPreview = ((paymentsInCycle - 1) % 3) + 1;
  if (
    uplineProgram.programId === "AFP-000005" &&
    level === 1 &&
    paymentIndexPreview === 1 &&
    fillerProgramId !== "AFP-000014" &&
    fillSource !== ROOT_SPAWN_FILL_SOURCE
  ) {
    return {
      filled: false,
      reason: "mahesh_L1_first_payment_requires_AFP-000014_spawn",
    };
  }
  if (
    uplineProgram.programId === "AFP-000005" &&
    level === 1 &&
    paymentIndexPreview > 1 &&
    !MAHESH_L1_ROSTER_UPGRADE_FILLERS.includes(fillerProgramId)
  ) {
    return {
      filled: false,
      reason: "mahesh_L1_upgrade_payments_require_AFP-000015_and_000016",
    };
  }
  if (isMaheshL2SerialFlow(uplineProgram.programId, level)) {
    const expectedFiller = expectedMaheshL2FillerProgramId(paymentIndexPreview);
    if (expectedFiller && fillerProgramId !== expectedFiller) {
      return {
        filled: false,
        reason: `mahesh_L2_payment_${paymentIndexPreview}_requires_${expectedFiller}`,
      };
    }
  }

  const levelAmount = getLevelAmount(level);
  const userDoc = await getUserDocByUid(uplineProgram.userId);
  if (!userDoc) return { filled: false, reason: "upline_user_missing" };

  await updateDoc(doc(db, "autoFillMatrixPositions", position.id), {
    positionStatus: "filled",
    filledByUserId: fillerUserId,
    filledByProgramId: fillerProgramId,
    filledAt: new Date().toISOString(),
    orderId: orderId || null,
    fillSource: fillSource || "AFP_ENROLLMENT",
    matrixFillSeq: matrixFillSeq ?? null,
  });

  const paymentIndex = paymentIndexPreview;
  const maheshL2Serial = isMaheshL2SerialFlow(uplineProgram.programId, level);

  const meta = {
    programId: uplineProgram.programId,
    userId: uplineProgram.userId,
    level,
    matrixPositionId: position.id,
    fillerUserId,
    reference: orderId,
  };

  const isFinalLevel = level >= AUTO_FILL_MAX_LEVEL;
  const paymentMeta = {
    ...meta,
    metadata: {
      ...(meta.metadata || {}),
      matrixPaymentIndex: paymentIndex,
      fillSource: fillSource || "AFP_ENROLLMENT",
      fillerProgramId: fillerProgramId || null,
    },
  };

  if (maheshL2Serial) {
    const routed = await applyMaheshL2SerialPaymentCredit({
      paymentIndex,
      amount: levelAmount,
      fillerProgramId,
      maheshProgramId: uplineProgram.programId,
      orderId,
      matrixPositionId: position.id,
      fillerUserId,
    });
    if (!routed.ok) {
      return { filled: false, reason: routed.reason || "mahesh_L2_serial_routing_failed" };
    }
  } else if (isFinalLevel) {
    await creditUserPointsWallet(
      doc(db, "users", userDoc.id),
      userDoc.data,
      levelAmount,
      paymentMeta
    );
    const forfeitureMin = resolveForfeitureLevelMin(uplineProgram);
    if (level >= forfeitureMin && level <= AUTO_FILL_FORFEITURE_LEVEL_MAX) {
      await recordLevelPointsCredited(
        uplineProgramDocId,
        uplineProgram,
        level,
        levelAmount
      );
    }
  } else if (paymentIndex === 1) {
    await creditUserPointsWallet(
      doc(db, "users", userDoc.id),
      userDoc.data,
      levelAmount,
      paymentMeta
    );
    const forfeitureMin = resolveForfeitureLevelMin(uplineProgram);
    if (level >= forfeitureMin && level <= AUTO_FILL_FORFEITURE_LEVEL_MAX) {
      await recordLevelPointsCredited(
        uplineProgramDocId,
        uplineProgram,
        level,
        levelAmount
      );
    }
  } else if (paymentIndex === 2) {
    await creditUserUpgradePool(
      doc(db, "users", userDoc.id),
      userDoc.data,
      levelAmount,
      {
        ...paymentMeta,
        metadata: {
          ...paymentMeta.metadata,
          role: "LEVEL_UNLOCK_RESERVE",
        },
      }
    );
  } else {
    await creditUserUpgradePool(
      doc(db, "users", userDoc.id),
      userDoc.data,
      levelAmount,
      {
        ...paymentMeta,
        metadata: {
          ...paymentMeta.metadata,
          role: "LEVEL_UNLOCK_FINAL",
        },
      }
    );
  }

  const programUpdate = {
    levelPaymentsInCycle: paymentsInCycle,
    updatedAt: new Date().toISOString(),
  };

  await updateDoc(doc(db, "autoFillPrograms", uplineProgramDocId), programUpdate);

  const refreshed = {
    ...uplineProgram,
    ...programUpdate,
  };

  if (paymentsInCycle >= 3) {
    await updateDoc(doc(db, "autoFillPrograms", uplineProgramDocId), {
      levelPaymentsInCycle: 0,
      updatedAt: new Date().toISOString(),
    });
    const freshUser = await getUserDocByUid(uplineProgram.userId);
    if (
      uplineProgram.programId === MAHESH_AUTO_FILL_PROGRAM_ID &&
      level === 2 &&
      (await maheshL2SerialCycleFilled(uplineProgramDocId))
    ) {
      const nextLevel = 3;
      await updateDoc(doc(db, "autoFillPrograms", uplineProgramDocId), {
        currentLevel: nextLevel,
        maheshL2SerialFlowDone: true,
        updatedAt: new Date().toISOString(),
      });
      await createLevelPositions(
        uplineProgramDocId,
        uplineProgram.programId,
        uplineProgram.userId,
        nextLevel
      );
      await recordAutoFillLedgerEntry({
        transactionId: buildTransactionId(),
        programId: uplineProgram.programId,
        userId: uplineProgram.userId,
        level: nextLevel,
        amount: 0,
        transactionType: "LEVEL_ACTIVATED",
        fromWallet: "SERIAL_FLOW",
        toWallet: "LEVEL",
        reference: "Mahesh L2 serial cycle (014/015/016) → Level 3",
        metadata: {
          maheshL2SerialFlow: true,
          kind: "MAHESH_L2_SERIAL_LEVEL_ADVANCE",
        },
      });
      const { routeMemberLevelActivationMatrixFill } = await import(
        "./autoFillActivationMatrixRouting.js"
      );
      await routeMemberLevelActivationMatrixFill({
        activatingProgram: {
          ...uplineProgram,
          currentLevel: nextLevel,
          maheshL2SerialFlowDone: true,
        },
        activatingProgramDocId: uplineProgramDocId,
        fromLevel: 2,
        processMatrixFillForUpline,
        findActiveProgramByEnrollmentSeq,
      });
    } else {
      await tryAdvanceProgramLevel(uplineProgramDocId, refreshed, freshUser);
    }
  }

  return { filled: true, paymentIndex, levelAmount };
}

export async function createAutoFillProgram({
  userId,
  userEmail,
  userName,
  packageAmount,
  orderId,
  parentProgramId = null,
  spawnedFromWithdrawal = false,
  spawnedFromWithdrawalId = null,
  skipActiveProgramPointerUpdate = false,
  skipGlobalEnrollmentMatrix = false,
  autoSecondOfRootSpawnPair = false,
}) {
  const { programPublicId, enrollmentSeq } = await allocateProgramIdentity();
  const userDoc = await getUserDocByUid(userId);
  if (!userDoc) throw new Error("User not found for Auto Fill program");
  const resolvedEmail = userEmail || userDoc.data.email;

  const programRef = await addDoc(collection(db, "autoFillPrograms"), {
    programId: programPublicId,
    enrollmentSeq,
    userId,
    userEmail: resolvedEmail,
    userName: userName || userDoc.data.name,
    packageAmount: Number(packageAmount),
    currentLevel: 1,
    status: "active",
    levelPaymentsInCycle: 0,
    parentProgramId,
    spawnedFromWithdrawal: spawnedFromWithdrawal === true,
    spawnedFromWithdrawalId: spawnedFromWithdrawalId || null,
    sourceOrderId: orderId || null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  await createLevelPositions(
    programRef.id,
    programPublicId,
    userId,
    1
  );

  if (!skipActiveProgramPointerUpdate) {
    if (!userDoc.data.autoFillPoints) {
      await updateDoc(doc(db, "users", userDoc.id), {
        autoFillPoints: { earned: 0, available: 0, withdrawn: 0, locked: 0 },
        autoFillUpgrade: { reserved: 0, used: 0 },
        activeAutoFillProgramId: programPublicId,
        updatedAt: new Date().toISOString(),
      });
    } else {
      await updateDoc(doc(db, "users", userDoc.id), {
        activeAutoFillProgramId: programPublicId,
        updatedAt: new Date().toISOString(),
      });
    }
  }

  await recordAutoFillLedgerEntry({
    transactionId: buildTransactionId(),
    programId: programPublicId,
    userId,
    level: 1,
    amount: Number(packageAmount),
    transactionType: "PROGRAM_CREATED",
    fromWallet: "EXTERNAL",
    toWallet: "PROGRAM",
    reference: orderId,
  });

  if (
    enrollmentSeq === 1 &&
    resolvedEmail === AUTO_FILL_COMPANY_EMAIL
  ) {
    await creditGenesisAdminCoPoints(Number(packageAmount), {
      programId: programPublicId,
      userId,
      level: 1,
      reference: orderId || "Genesis AFP entry",
    });
  }

  if (
    !skipGlobalEnrollmentMatrix &&
    enrollmentSeq > 1 &&
    spawnedFromWithdrawal !== true
  ) {
    const fillSeq = await registerMatrixFillForAfpEnrollment(enrollmentSeq);
    const uplineIndex = uplineEnrollmentIndexForMatrixFillSeq(fillSeq);
    const uplineId = `AFP-${String(uplineIndex).padStart(6, "0")}`;
    let upline = null;
    const byIdSnap = await getDocs(
      query(
        collection(db, "autoFillPrograms"),
        where("programId", "==", uplineId)
      )
    );
    if (!byIdSnap.empty) {
      const d = byIdSnap.docs[0];
      upline = { id: d.id, ...d.data() };
    } else {
      upline = await findActiveProgramByEnrollmentSeq(uplineIndex);
    }
    if (upline) {
      await processMatrixFillForUpline({
        uplineProgram: upline,
        uplineProgramDocId: upline.id,
        fillerUserId: userId,
        fillerProgramId: programPublicId,
        orderId,
        fillSource: "AFP_ENROLLMENT",
        matrixFillSeq: fillSeq,
      });
    }
  }

  if (spawnedFromWithdrawal === true && parentProgramId) {
    const { runWithdrawalSpawnMatrixIfApplicable } = await import(
      "./autoFillWithdrawalSpawnMatrix.js"
    );
    await runWithdrawalSpawnMatrixIfApplicable(
      programRef.id,
      {
        programId: programPublicId,
        enrollmentSeq,
        userId,
        userEmail: resolvedEmail,
        parentProgramId,
        spawnedFromWithdrawal: true,
        sourceOrderId: orderId || null,
        createdAt: new Date().toISOString(),
      },
      { orderId }
    );
  }

  return { programDocId: programRef.id, programId: programPublicId };
}
